import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Play, ChevronLeft, ChevronRight, X, Paperclip, Eye, Loader2, AlertCircle, Plus, Save } from 'lucide-react'
import { Field } from './Field'
import { Button } from './ui/button'
import { Select, SelectItem } from './ui/select'
import { ComponentCard } from './ui/card'
import { searchCoffeesByIdentifier, checkValueUnique } from '../api/brainer'
import { uploadDocument, viewDocumentInTab, getDocument } from '../api/storage'
import { loadGlobalOptions, toTargets } from '../api/global-options'
import { useAuth } from '../store/auth'
import type {
  BJourneyInterviewerForm, BJourneyWizardTraitItem,
  BJourneyWizardRelationItem, BCupRelationType, BCoffeeValidationError, BJourneyWizardGlobalItem, BGlobalTarget,
} from '../types'

const MIN_QUERY_LEN = 3

// ── Types ─────────────────────────────────────────────────────────────────────

type TraitAnswers  = Record<string, string>
type RelationAnswer = {
  type: BCupRelationType
  parentCoffeeId?: string
  parentCoffeeIds?: string[]
  beans?: Record<string, string>
}
type RelationAnswers = Record<string, RelationAnswer>
// Keyed by item.id (not cupTraitId/cupRelationId — a chain's far trait/relation id could collide
// across different chains) — only populated for items with viaCupRelationIds. A document-type
// chained trait answer carries the upload's filename alongside its id so the backend can sync
// the far cup's identifier trait; every other chained answer is shaped just like its non-chained
// counterpart (plain string for a trait, RelationAnswer for a relation).
type ChainedAnswer = string | { documentId: string; filename: string } | RelationAnswer
type ChainedAnswers = Record<string, ChainedAnswer>
// Keyed by global relation id: the picked targets (e.g. a shop's mailbox).
type GlobalAnswers = Record<string, BGlobalTarget[]>

interface Props {
  form: BJourneyInterviewerForm
  onSubmit: (traits: TraitAnswers, relations: RelationAnswers, chainedAnswers: ChainedAnswers, globals: GlobalAnswers) => void
  onCancel: () => void
  isPending: boolean
  // Lets a 'live_search' relation item whose search comes up empty offer a "create new" link —
  // see RelationWidget/LiveSearchWidget's onCreateNew. Omit to skip that affordance entirely.
  navigateTo?: (plateId: string, paramName: string, value: string) => void
  // Per-field errors from the server's 422 on the last submit — mapped onto fields by
  // cupTraitId/cupRelationId (see the serverErrors effect below).
  serverErrors?: BCoffeeValidationError[]
  // Called by a relation item's alternate-action link (altPreparationId) — the caller runs that
  // Preparation (e.g. "+ Add one-time article" from Add Preorder Element).
  onAltAction?: (preparationId: string, label: string) => void
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function ingredientWidget(name?: string): 'text' | 'number' | 'date' | 'checkbox' | 'collection' | 'document' {
  const n = (name ?? '').toLowerCase()
  if (n === 'boolean')                          return 'checkbox'
  if (n === 'integer' || n === 'number' || n.startsWith('decimal')) return 'number'
  if (n === 'date')                             return 'date'
  if (n === 'array')                            return 'collection'
  if (n === '_document' || n === '_documentpath') return 'document'
  return 'text'
}

// ── Document widget ───────────────────────────────────────────────────────────

function DocumentWidget({ item, value, onChange, onUploadedMeta, storageDatasetId }: {
  item: BJourneyWizardTraitItem
  value: string
  onChange: (v: string) => void
  // Fired right after onChange with the same upload's original filename — only chained
  // (viaCupRelationIds) callers pass this, to sync the far cup's identifier trait on submit.
  // Every existing non-chained caller omits it, so their behavior is unchanged.
  onUploadedMeta?: (documentId: string, filename: string) => void
  storageDatasetId?: string | null
}) {
  const [uploading, setUploading]   = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [docTitle, setDocTitle]     = useState<string | null>(null)
  const [viewing, setViewing]       = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  // Fetch document title when we have an existing value
  useEffect(() => {
    if (!value) { setDocTitle(null); return }
    getDocument(value)
      .then(r => setDocTitle(r.data.title))
      .catch(() => setDocTitle(null))
  }, [value])

  const handleFile = async (file: File) => {
    if (!storageDatasetId) {
      setUploadError('No storage dataset configured on this interviewer. Ask an admin to set one.')
      return
    }
    setUploading(true)
    setUploadError('')
    try {
      const res = await uploadDocument({ datasetId: storageDatasetId, title: file.name, file })
      onChange(res.data.id)
      setDocTitle(res.data.title)
      onUploadedMeta?.(res.data.id, res.data.title)
    } catch {
      setUploadError('Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleView = async () => {
    if (!value) return
    setViewing(true)
    try { await viewDocumentInTab(value) }
    catch { /* ignore */ }
    finally { setViewing(false) }
  }

  return (
    <Field label={`${item.title}${item.mandatory ? ' *' : ''}`} description={item.description}>
      <div className="space-y-2">
        {value && (
          <div className="flex items-center gap-2 px-3 py-2 bg-paper-sunken border border-paper-border rounded-lg">
            <Paperclip size={13} className="text-ink-dim shrink-0" />
            <span className="text-sm text-ink flex-1 truncate">{docTitle ?? value.slice(0, 8) + '…'}</span>
            <button
              type="button"
              onClick={handleView}
              disabled={viewing}
              className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover transition-colors disabled:opacity-50"
            >
              {viewing ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />}
              View
            </button>
            <button
              type="button"
              onClick={() => { onChange(''); setDocTitle(null); if (fileRef.current) fileRef.current.value = '' }}
              className="text-ink-dim hover:text-red-500 transition-colors"
            >
              <X size={13} />
            </button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            id={`doc-upload-${item.cupTraitId}`}
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
          />
          <label
            htmlFor={`doc-upload-${item.cupTraitId}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border cursor-pointer transition-colors ${
              uploading
                ? 'bg-paper-sunken text-ink-dim border-paper-border cursor-not-allowed'
                : 'bg-white text-ink-dim border-paper-border hover:bg-paper-sunken'
            }`}
          >
            {uploading
              ? <><Loader2 size={11} className="animate-spin" /> Uploading…</>
              : <><Paperclip size={11} /> {value ? 'Replace document' : 'Attach document'}</>
            }
          </label>
          {!storageDatasetId && (
            <span className="flex items-center gap-1 text-xs text-amber-600">
              <AlertCircle size={11} /> No dataset configured
            </span>
          )}
        </div>
        {uploadError && <p className="text-xs text-red-500">{uploadError}</p>}
      </div>
    </Field>
  )
}

// ── Trait widget ──────────────────────────────────────────────────────────────

function TraitWidget({ item, value, onChange, onUploadedMeta, storageDatasetId }: {
  item: BJourneyWizardTraitItem
  value: string
  onChange: (v: string) => void
  onUploadedMeta?: (documentId: string, filename: string) => void
  storageDatasetId?: string | null
}) {
  const widget = ingredientWidget(item.ingredientName)
  const items  = item.collectionItems ?? []

  if (widget === 'checkbox') {
    return (
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={e => onChange(e.target.checked ? 'true' : 'false')}
          className="rounded w-4 h-4"
        />
        <span className="text-sm font-medium text-ink">
          {item.title}{item.mandatory && <span className="ml-1 text-red-400">*</span>}
        </span>
        {item.description && <span className="text-theme-xs text-ink-dim">{item.description}</span>}
      </label>
    )
  }

  if (widget === 'document') {
    return (
      <DocumentWidget
        item={item}
        value={value}
        onChange={onChange}
        onUploadedMeta={onUploadedMeta}
        storageDatasetId={storageDatasetId}
      />
    )
  }

  if (widget === 'collection') {
    const useRadio = items.length < 5
    return (
      <Field label={`${item.title}${item.mandatory ? ' *' : ''}`} description={item.description}>
        {useRadio ? (
          <div className="grid grid-cols-2 gap-2">
            {items.map(opt => (
              <button key={opt.id} type="button"
                onClick={() => onChange(opt.value)}
                className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                  value === opt.value
                    ? 'border-accent bg-accent-soft text-accent ring-1 ring-accent'
                    : 'border-paper-border bg-white text-ink hover:border-ink-dim/40'
                }`}
              >
                {opt.value}
              </button>
            ))}
          </div>
        ) : (
          <Select value={value} onValueChange={onChange} placeholder="— select —">
            {items.map(opt => <SelectItem key={opt.id} value={opt.value}>{opt.value}</SelectItem>)}
          </Select>
        )}
      </Field>
    )
  }

  if (item.inputMode === 'textarea' && widget === 'text') {
    return (
      <Field label={`${item.title}${item.mandatory ? ' *' : ''}`} description={item.description}>
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={7} className="field" />
      </Field>
    )
  }

  return (
    <Field label={`${item.title}${item.mandatory ? ' *' : ''}`} description={item.description}>
      <input
        type={widget}
        step={widget === 'number' ? 'any' : undefined}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={item.currentValue ?? ''}
        className="field"
      />
    </Field>
  )
}

// ── Live search widget ────────────────────────────────────────────────────────

function LiveSearchWidget({ cupId, multi = false, selectedId, selectedIds = [], onChangeSingle, onChangeMulti, onCreateNew, traitTitles }: {
  cupId: string
  multi?: boolean
  selectedId?: string
  selectedIds?: string[]
  onChangeSingle?: (id: string | undefined) => void
  onChangeMulti?: (ids: string[]) => void
  // Shown below the results (single-select only) once the search comes up empty — lets the user
  // jump straight to creating the record they couldn't find instead of getting stuck.
  onCreateNew?: () => void
  // Search/display across these trait titles instead of the cup's single identifier trait — see
  // BJourneyWizardRelationItem.searchFields.
  traitTitles?: string[]
}) {
  const [query, setQuery]           = useState('')
  const [results, setResults]       = useState<{ id: string; label: string }[]>([])
  const [open, setOpen]             = useState(false)
  const [pickedLabel, setPickedLabel] = useState<string | undefined>()
  const [pickedLabels, setPickedLabels] = useState<Record<string, string>>({})
  const debounceRef  = useRef<ReturnType<typeof setTimeout>>()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    clearTimeout(debounceRef.current)
    // Below the minimum, skip the request entirely — no bulk-loading the cup's records just
    // because the field gained focus (searchCoffeesByIdentifier has no length guard of its own
    // and an empty query matches everything, capped only by its own hardcoded LIMIT 20).
    if (!open || query.trim().length < MIN_QUERY_LEN) { setResults([]); return }
    debounceRef.current = setTimeout(async () => {
      try { setResults((await searchCoffeesByIdentifier(cupId, query, traitTitles)).data) }
      catch { setResults([]) }
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [query, open, cupId, traitTitles])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  if (!multi) {
    const hasSelection = !!(selectedId || pickedLabel)
    const select = (item: { id: string; label: string }) => {
      setPickedLabel(item.label)
      setQuery(''); setOpen(false); setResults([])
      onChangeSingle?.(item.id)
    }
    const clear = () => { setPickedLabel(undefined); onChangeSingle?.(undefined) }

    return (
      <div ref={containerRef} className="relative">
        {hasSelection ? (
          <div className="flex items-center gap-2 px-3 py-2 border border-accent/20 bg-accent-soft rounded-lg">
            <span className="text-sm text-accent flex-1 truncate">
              {pickedLabel ?? (selectedId ? selectedId.slice(0, 8) + '…' : '')}
            </span>
            <button onClick={clear} type="button" className="text-accent/60 hover:text-accent">
              <X size={13} />
            </button>
          </div>
        ) : (
          <>
            <input
              type="text" value={query}
              onChange={e => { setQuery(e.target.value); setOpen(true) }}
              onFocus={() => setOpen(true)}
              placeholder="Type to search…"
              className="field"
            />
            {open && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-paper-border rounded-lg shadow-dropdown max-h-48 overflow-y-auto">
                {results.length === 0
                  ? <p className="text-xs text-ink-dim px-3 py-2">{query.trim().length < MIN_QUERY_LEN ? `Type at least ${MIN_QUERY_LEN} characters to search…` : 'No results'}</p>
                  : results.map(r => (
                    <button key={r.id} type="button" onMouseDown={() => select(r)}
                      className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-accent-soft">
                      {r.label}
                    </button>
                  ))
                }
                {onCreateNew && results.length === 0 && query.trim().length >= MIN_QUERY_LEN && (
                  <button type="button" onMouseDown={onCreateNew}
                    className="w-full text-left px-3 py-2 text-sm text-accent hover:bg-accent-soft border-t border-paper-border">
                    + Create new
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  // Multi select
  const toggle = (item: { id: string; label: string }) => {
    setPickedLabels(prev => ({ ...prev, [item.id]: item.label }))
    const next = selectedIds.includes(item.id)
      ? selectedIds.filter(x => x !== item.id)
      : [...selectedIds, item.id]
    onChangeMulti?.(next)
  }
  const remove = (id: string) => onChangeMulti?.(selectedIds.filter(x => x !== id))

  return (
    <div ref={containerRef} className="space-y-2">
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedIds.map(id => (
            <span key={id} className="flex items-center gap-1 px-2 py-1 bg-accent-soft text-accent text-xs rounded-full">
              {pickedLabels[id] ?? id.slice(0, 8) + '…'}
              <button onClick={() => remove(id)} type="button" className="text-accent/60 hover:text-accent ml-0.5">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          type="text" value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          placeholder="Type to search and add…"
          className="field"
        />
        {open && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-paper-border rounded-lg shadow-dropdown max-h-48 overflow-y-auto">
            {results.length === 0
              ? <p className="text-xs text-ink-dim px-3 py-2">{query.trim().length < MIN_QUERY_LEN ? `Type at least ${MIN_QUERY_LEN} characters to search…` : 'No results'}</p>
              : results.map(r => (
                <button key={r.id} type="button" onMouseDown={() => toggle(r)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-accent-soft flex items-center gap-2 ${
                    selectedIds.includes(r.id) ? 'text-accent font-medium' : 'text-ink'
                  }`}>
                  {selectedIds.includes(r.id) && <span className="text-accent text-xs">✓</span>}
                  {r.label}
                </button>
              ))
            }
          </div>
        )}
      </div>
    </div>
  )
}

// ── Relation widget ───────────────────────────────────────────────────────────

function RelationWidget({ item, answer, onChange, onCreateNew }: {
  item: BJourneyWizardRelationItem
  answer: RelationAnswer | undefined
  onChange: (a: RelationAnswer) => void
  onCreateNew?: () => void
}) {
  const options      = item.options ?? []
  const parentTraits = item.parentTraits ?? []
  const labelText    = item.label ?? 'Related record'
  const mark         = item.mandatory ? ' *' : ''

  if (item.relationType === 'belongs_to') {
    if (item.inputMode === 'live_search') {
      return (
        <Field label={`${labelText}${mark}`}>
          <LiveSearchWidget
            cupId={item.parentCupId}
            selectedId={answer?.parentCoffeeId ?? (item.currentValue ?? undefined)}
            onChangeSingle={id => onChange({ type: 'belongs_to', parentCoffeeId: id })}
            onCreateNew={onCreateNew}
            traitTitles={item.searchFields}
          />
        </Field>
      )
    }
    return (
      <Field label={`${labelText}${mark}`}>
        <Select
          value={answer?.parentCoffeeId ?? (item.currentValue ?? '')}
          onValueChange={v => onChange({ type: 'belongs_to', parentCoffeeId: v || undefined })}
          placeholder="— none —"
        >
          {options.map(opt => <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>)}
        </Select>
      </Field>
    )
  }

  if (item.relationType === 'many_to_many') {
    const selected = answer?.parentCoffeeIds ?? (item.currentValue ? item.currentValue.split(',').filter(Boolean) : [])
    if (item.inputMode === 'live_search') {
      return (
        <Field label={`${labelText}${mark}`}>
          <LiveSearchWidget
            cupId={item.parentCupId}
            multi
            selectedIds={selected}
            onChangeMulti={ids => onChange({ type: 'many_to_many', parentCoffeeIds: ids })}
            traitTitles={item.searchFields}
          />
        </Field>
      )
    }
    const toggle = (id: string) => {
      const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]
      onChange({ type: 'many_to_many', parentCoffeeIds: next })
    }
    return (
      <Field label={`${labelText}${mark}`}>
        <div className="space-y-1">
          {options.map(opt => (
            <label key={opt.id} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={selected.includes(opt.id)} onChange={() => toggle(opt.id)} className="rounded w-4 h-4" />
              <span className="text-sm text-ink">{opt.label}</span>
            </label>
          ))}
          {options.length === 0 && <p className="text-xs text-ink-dim">No existing records</p>}
        </div>
      </Field>
    )
  }

  // owns — inline sub-form to create parent
  const beans = answer?.beans ?? {}
  const setBeans = (b: Record<string, string>) => onChange({ type: 'owns', beans: b })

  return (
    <div className="border border-paper-border rounded-lg p-3 space-y-3">
      <p className="text-xs font-semibold text-ink-dim">{labelText}{mark}</p>
      {parentTraits.map(pt => (
        <Field key={pt.cupTraitId} label={`${pt.title}${pt.mandatory ? ' *' : ''}`} description={pt.description}>
          {pt.collectionItems && pt.collectionItems.length > 0 ? (
            <Select
              value={beans[pt.cupTraitId] ?? ''}
              onValueChange={v => setBeans({ ...beans, [pt.cupTraitId]: v })}
              placeholder="— select —"
            >
              {pt.collectionItems.map(ci => <SelectItem key={ci.id} value={ci.value}>{ci.value}</SelectItem>)}
            </Select>
          ) : (
            <input
              value={beans[pt.cupTraitId] ?? ''}
              onChange={e => setBeans({ ...beans, [pt.cupTraitId]: e.target.value })}
              className="field"
              placeholder={pt.ingredientName === 'boolean' ? 'true/false' : ''}
            />
          )}
        </Field>
      ))}
      {parentTraits.length === 0 && <p className="text-xs text-ink-dim">No traits configured on parent cup</p>}
    </div>
  )
}

// ── Global relation widget ────────────────────────────────────────────────────
// Picks existing records of another service (a mailbox, users, datasets) — never creates them.
function GlobalWidget({ item, value, onChange }: {
  item: BJourneyWizardGlobalItem
  value: BGlobalTarget[]
  onChange: (targets: BGlobalTarget[]) => void
}) {
  const { user } = useAuth()
  const { data: options = [], isLoading, isError } = useQuery({
    queryKey: ['global-options', item.targetClass, user?.businessId],
    queryFn: () => loadGlobalOptions(item.targetClass, user?.businessId),
  })
  const label = `${item.label}${item.mandatory ? ' *' : ''}`
  const ids = value.map(t => t.id)
  if (isError) return <Field label={label}><p className="text-sm text-error-500">Couldn't load the list.</p></Field>
  if (item.relationType === 'belongs_to') {
    return (
      <Field label={label}>
        <Select
          value={ids[0] ?? ''}
          onValueChange={v => onChange(v ? toTargets(options, [v]) : [])}
          placeholder={isLoading ? 'Loading…' : '— none —'}
        >
          {options.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
        </Select>
      </Field>
    )
  }
  const toggle = (id: string) => onChange(toTargets(options, ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]))
  return (
    <Field label={label}>
      <div className="space-y-1.5">
        {options.map(o => (
          <label key={o.id} className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={ids.includes(o.id)} onChange={() => toggle(o.id)} className="rounded w-4 h-4" />
            <span className="text-sm text-gray-700">{o.label}</span>
          </label>
        ))}
        {!isLoading && options.length === 0 && <p className="text-xs text-gray-500">None available</p>}
      </div>
    </Field>
  )
}

// ── Validation helpers ────────────────────────────────────────────────────────

type WizardItem = BJourneyWizardTraitItem | BJourneyWizardRelationItem | BJourneyWizardGlobalItem
type FieldErrors = Record<string, string> // keyed by item.id

// "Garage Article.sellingprice" -> "Sellingprice", "category_id" -> "Category" — same shape as
// the server's messages (brainer coffee-validation.ts) so client and server errors read alike.
function fieldName(item: WizardItem): string {
  const raw = item.type === 'trait' ? item.title : (item.label ?? 'This field')
  const f = (raw.split('.').pop() ?? raw).trim().replace(/_id$/, '').replace(/_/g, ' ')
  return f.charAt(0).toUpperCase() + f.slice(1)
}

// Fields that need the full row in the 'page' layout's two-column grid.
function isWide(item: WizardItem): boolean {
  if (item.type === 'relation' || item.type === 'global') return item.relationType !== 'belongs_to'
  if (item.inputMode === 'textarea') return true
  const w = ingredientWidget(item.ingredientName)
  return w === 'document' || w === 'checkbox' || (w === 'collection' && (item.collectionItems?.length ?? 0) < 5)
}

// ── InterviewerWizard ─────────────────────────────────────────────────────────

export function InterviewerWizard({ form, onSubmit, onCancel, isPending, navigateTo, serverErrors, onAltAction }: Props) {
  const parts = form.parts
  const pageLayout = form.layout === 'page'
  const [partIdx, setPartIdx] = useState(0)
  const [traits,  setTraits]  = useState<TraitAnswers>(() => {
    const init: TraitAnswers = {}
    for (const part of parts) {
      for (const item of part.items) {
        if (item.type === 'trait' && !item.viaCupRelationIds?.length && item.currentValue !== undefined) {
          init[item.cupTraitId] = item.currentValue
        }
      }
    }
    return init
  })
  const [relations, setRelations] = useState<RelationAnswers>(() => {
    const init: RelationAnswers = {}
    for (const part of parts) {
      for (const item of part.items) {
        if (item.type === 'relation' && !item.viaCupRelationIds?.length && item.currentValue) {
          init[item.cupRelationId] = { type: item.relationType, parentCoffeeId: item.currentValue }
        }
      }
    }
    return init
  })
  const [globals, setGlobals] = useState<GlobalAnswers>(() => {
    const init: GlobalAnswers = {}
    for (const part of parts) {
      for (const item of part.items) {
        if (item.type === 'global') init[item.globalRelationId] = item.currentValue ?? []
      }
    }
    return init
  })
  const [chainedAnswers, setChainedAnswers] = useState<ChainedAnswers>(() => {
    const init: ChainedAnswers = {}
    for (const part of parts) {
      for (const item of part.items) {
        if (!item.viaCupRelationIds?.length) continue
        if (item.type === 'trait' && item.currentValue !== undefined) {
          init[item.id] = item.currentValue
        } else if (item.type === 'relation' && item.currentValue) {
          init[item.id] = { type: item.relationType, parentCoffeeId: item.currentValue }
        }
      }
    }
    return init
  })

  // Per-field errors from three sources: the required check (on blur and on Next/Submit), the
  // live uniqueness check (on blur), and the server's 422 (serverErrors). Editing a field clears
  // its error; Submit stays disabled while any remain.
  const [errors, setErrors] = useState<FieldErrors>({})
  // Server errors that don't belong to any visible field (e.g. a required relation the form
  // doesn't ask for) — shown above the form since no field edit can clear them.
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [checking, setChecking] = useState(0)
  const lastChecked = useRef<Record<string, string>>({})

  const allItems = parts.flatMap(p => p.items) as WizardItem[]
  const visible = (item: WizardItem) => !(item.type === 'relation' && item.inputMode === 'from_payload')

  useEffect(() => {
    if (!serverErrors?.length) return
    const mapped: FieldErrors = {}
    const unmatched: string[] = []
    for (const err of serverErrors) {
      const item = allItems.find(i => visible(i) && (
        (i.type === 'trait' && err.cupTraitId && i.cupTraitId === err.cupTraitId) ||
        (i.type === 'relation' && err.cupRelationId && i.cupRelationId === err.cupRelationId) ||
        (i.type === 'global' && err.globalRelationId && i.globalRelationId === err.globalRelationId)
      ))
      if (item) mapped[item.id] = err.message
      else unmatched.push(err.message)
    }
    setErrors(prev => ({ ...prev, ...mapped }))
    setFormErrors(unmatched)
    if (!pageLayout) {
      const first = parts.findIndex(p => p.items.some(i => mapped[i.id]))
      if (first >= 0) setPartIdx(first)
    }
    // allItems/parts derive from form, which doesn't change under a mounted wizard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverErrors])

  const clearError = (id: string) => {
    setErrors(prev => {
      if (!(id in prev)) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const traitValue = (t: BJourneyWizardTraitItem): string => {
    if (t.viaCupRelationIds?.length) {
      const raw = chainedAnswers[t.id]
      const chunk = typeof raw === 'object' && raw && 'documentId' in raw ? raw.documentId : (raw as string | undefined)
      return chunk ?? t.currentValue ?? ''
    }
    return traits[t.cupTraitId] ?? t.currentValue ?? ''
  }

  const isMissing = (item: WizardItem): boolean => {
    if (!item.mandatory || !visible(item)) return false
    if (item.type === 'trait') {
      if (ingredientWidget(item.ingredientName) === 'checkbox') return false
      return traitValue(item).trim() === ''
    }
    if (item.type === 'global') return (globals[item.globalRelationId] ?? []).length === 0
    const ans = item.viaCupRelationIds?.length
      ? chainedAnswers[item.id] as RelationAnswer | undefined
      : relations[item.cupRelationId]
    if (item.relationType === 'belongs_to') return !ans?.parentCoffeeId
    if (item.relationType === 'many_to_many') {
      const sel = ans?.parentCoffeeIds ?? (item.currentValue ? item.currentValue.split(',').filter(Boolean) : [])
      return sel.length === 0
    }
    return false // 'owns' — sub-form, validated server-side
  }

  // Runs the required check over the given items; returns true when they're all filled in.
  const validate = (items: WizardItem[]): boolean => {
    const found: FieldErrors = {}
    for (const item of items) if (isMissing(item)) found[item.id] = `${fieldName(item)} is required`
    if (Object.keys(found).length === 0) return true
    setErrors(prev => ({ ...prev, ...found }))
    return false
  }

  const checkUnique = (t: BJourneyWizardTraitItem) => {
    const value = traitValue(t).trim()
    if (!t.uniqueField || !value) return
    lastChecked.current[t.id] = value
    setChecking(n => n + 1)
    checkValueUnique(t.cupTraitId, value, t.uniqueExcludeCoffeeId)
      .then(r => {
        // Ignore a stale answer if the field changed while the request was in flight.
        if (lastChecked.current[t.id] !== value || r.data.unique) return
        setErrors(prev => ({ ...prev, [t.id]: `"${value}" is already used by another record` }))
      })
      .catch(() => { /* the server re-checks on submit */ })
      .finally(() => setChecking(n => n - 1))
  }

  const handleBlur = (item: WizardItem) => {
    if (item.type !== 'trait') return
    const w = ingredientWidget(item.ingredientName)
    if (w !== 'text' && w !== 'number' && w !== 'date') return
    if (isMissing(item)) setErrors(prev => ({ ...prev, [item.id]: `${fieldName(item)} is required` }))
    else checkUnique(item)
  }

  if (parts.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-ink-dim">This interviewer has no parts configured yet.</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        </div>
      </div>
    )
  }

  const currentPart = parts[partIdx]
  const isFirst = partIdx === 0
  const isLast  = partIdx === parts.length - 1

  const hasErrors = (items: WizardItem[]) => items.some(i => errors[i.id])
  const blocked = hasErrors(allItems) || checking > 0 || isPending

  const goNext = () => {
    if (validate(currentPart.items as WizardItem[])) setPartIdx(i => i + 1)
  }

  const handleSubmit = () => {
    if (!validate(allItems)) {
      if (!pageLayout) {
        const first = parts.findIndex(p => p.items.some(i => isMissing(i as WizardItem)))
        if (first >= 0) setPartIdx(first)
      }
      return
    }
    setFormErrors([])
    onSubmit(traits, relations, chainedAnswers, globals)
  }

  const createNewHandler = (r: BJourneyWizardRelationItem) =>
    r.createNewPlateId && navigateTo && form.coffeeId
      ? () => navigateTo!(r.createNewPlateId!, form.payloadKey, form.coffeeId!)
      : undefined

  const renderWidget = (item: WizardItem) => {
    if (item.type === 'global') {
      return (
        <GlobalWidget
          item={item}
          value={globals[item.globalRelationId] ?? []}
          onChange={t => { setGlobals(prev => ({ ...prev, [item.globalRelationId]: t })); clearError(item.id) }}
        />
      )
    }
    if (item.viaCupRelationIds?.length) {
      if (item.type === 'trait') {
        const raw = chainedAnswers[item.id]
        const displayValue = typeof raw === 'object' && raw && 'documentId' in raw ? raw.documentId : ((raw as string) ?? '')
        return (
          <TraitWidget
            item={item}
            value={displayValue}
            onChange={v => { setChainedAnswers(prev => ({ ...prev, [item.id]: v })); clearError(item.id) }}
            onUploadedMeta={(documentId, filename) => setChainedAnswers(prev => ({ ...prev, [item.id]: { documentId, filename } }))}
            storageDatasetId={form.storageDatasetId}
          />
        )
      }
      return (
        <RelationWidget
          item={item}
          answer={chainedAnswers[item.id] as RelationAnswer | undefined}
          onChange={a => { setChainedAnswers(prev => ({ ...prev, [item.id]: a })); clearError(item.id) }}
          onCreateNew={createNewHandler(item)}
        />
      )
    }
    if (item.type === 'trait') {
      return (
        <TraitWidget
          item={item}
          value={traits[item.cupTraitId] ?? ''}
          onChange={v => { setTraits(prev => ({ ...prev, [item.cupTraitId]: v })); clearError(item.id) }}
          storageDatasetId={form.storageDatasetId}
        />
      )
    }
    return (
      <RelationWidget
        item={item}
        answer={relations[item.cupRelationId]}
        onChange={a => { setRelations(prev => ({ ...prev, [item.cupRelationId]: a })); clearError(item.id) }}
        onCreateNew={createNewHandler(item)}
      />
    )
  }

  // Wraps a widget with its error message (TailAdmin: error-500 border + mt-1.5 text-xs hint)
  // and, for a relation, its optional alternate-action link.
  const renderItem = (item: WizardItem, extraClass = '') => {
    if (!visible(item)) return null
    const err = errors[item.id]
    const alt = item.type === 'relation' && item.altPreparationId && onAltAction ? item : null
    return (
      <div
        key={item.id}
        className={`${err ? 'field-invalid' : ''} ${extraClass}`.trim() || undefined}
        onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) handleBlur(item) }}
      >
        {renderWidget(item)}
        {err && <p className="mt-1.5 text-xs text-error-500">{err}</p>}
        {alt && (
          <button
            type="button"
            onClick={() => onAltAction!(alt.altPreparationId!, alt.altLabel || 'Create new')}
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand-500 hover:text-brand-600"
          >
            <Plus size={14} /> {alt.altLabel || 'Create new'}
          </button>
        )}
      </div>
    )
  }

  const formErrorBanner = formErrors.length > 0 && (
    <div className="flex gap-3 rounded-xl border border-error-500 bg-error-50 p-4">
      <AlertCircle size={20} className="mt-0.5 shrink-0 text-error-500" />
      <div className="text-sm text-gray-700">
        <p className="font-semibold text-gray-800">This record can't be saved yet</p>
        <ul className="mt-1 list-disc pl-4">{formErrors.map((m, i) => <li key={i}>{m}</li>)}</ul>
      </div>
    </div>
  )

  // ── 'page' layout: every part is a card section, one Save at the bottom ──
  if (pageLayout) {
    return (
      <div className="space-y-6">
        {formErrorBanner}
        {parts.map(part => {
          const items = (part.items as WizardItem[]).filter(visible)
          if (items.length === 0) return null
          return (
            <ComponentCard key={part.id} title={part.title}>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {items.map(item => renderItem(item, isWide(item) ? 'sm:col-span-2' : ''))}
              </div>
            </ComponentCard>
          )
        })}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button icon={Save} onClick={handleSubmit} disabled={blocked}>
            {isPending ? 'Saving…' : form.isUpdate ? 'Save changes' : 'Save'}
          </Button>
        </div>
      </div>
    )
  }

  // ── 'wizard' layout: one part per step ──
  return (
    <div className="space-y-4">
      {form.isUpdate && (
        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded px-3 py-2">
          Editing an existing record — fields show its current values.
        </p>
      )}
      {formErrorBanner}

      {/* Part tabs */}
      {parts.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {parts.map((p, i) => (
            <button
              key={p.id}
              onClick={() => i < partIdx ? setPartIdx(i) : i === partIdx + 1 ? goNext() : undefined}
              className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap border transition-colors ${
                i === partIdx
                  ? 'bg-accent text-white border-accent'
                  : hasErrors(p.items as WizardItem[])
                    ? 'bg-error-50 text-error-600 border-error-200 cursor-pointer'
                    : i < partIdx
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200 cursor-pointer hover:bg-emerald-100'
                      : 'bg-paper-sunken text-ink-dim border-paper-border cursor-default'
              }`}
            >
              {i + 1}. {p.title}
            </button>
          ))}
        </div>
      )}

      {/* Current part fields */}
      <div className="space-y-4 min-h-[120px]">
        {currentPart.items.length === 0 && (
          <p className="text-xs text-ink-dim text-center py-4">No questions in this part</p>
        )}
        {(currentPart.items as WizardItem[]).map(item => renderItem(item))}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2 border-t border-paper-border">
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>

        <div className="flex items-center gap-2">
          {!isFirst && (
            <Button variant="secondary" size="sm" icon={ChevronLeft} onClick={() => setPartIdx(i => i - 1)}>
              Back
            </Button>
          )}
          {!isLast ? (
            <Button
              size="sm"
              onClick={goNext}
              disabled={hasErrors(currentPart.items as WizardItem[]) || checking > 0}
            >
              Next <ChevronRight size={13} />
            </Button>
          ) : (
            <Button
              icon={Play}
              onClick={handleSubmit}
              disabled={blocked}
            >
              {isPending ? 'Submitting…' : form.isUpdate ? 'Update & continue' : 'Create & continue'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
